// Formulaire rendu à partir du schéma d'une démarche, avec le moteur partagé : visibilité des
// sections et des champs recalculée à chaque saisie (un champ masqué vaut non renseigné), valeurs
// par défaut, champs calculés, erreurs par champ sans perte de saisie. Pur rendu : la validation
// complète et l'enregistrement sont déclenchés par le parent.
import { validerDonnees, valeursParDefaut, visibilites, type DonneesFormulaire, type Formulaire } from '@cc/contrats';
import { useMemo } from 'react';
import { ChampDynamique, type PieceDisponible } from './ChampDynamique';
import type { ErreurSaisie } from './formulaire-depuis-fiche';
import styles from './depot.module.css';

export interface ProprietesFormulaireDynamique {
  formulaire: Formulaire;
  donnees: DonneesFormulaire;
  onChange: (donnees: DonneesFormulaire) => void;
  /** Appelé avec le code du champ modifié : le parent retire les erreurs qui le concernaient. */
  onChampModifie?: (code: string) => void;
  erreurs: ErreurSaisie[];
  tarifs?: Record<string, number>;
  pieces?: PieceDisponible[];
  /** Préfixe des identifiants des champs (liens du résumé des erreurs). */
  prefixe?: string;
  desactive?: boolean;
}

/** Données initiales : valeurs par défaut du schéma complétées par les données déjà enregistrées. */
export function donneesInitiales(f: Formulaire, enregistrees: DonneesFormulaire | undefined): DonneesFormulaire {
  return { ...valeursParDefaut(f), ...(enregistrees ?? {}) };
}

/** Identifiant d'un champ d'après son code (et celui des sous-éléments). */
export function idChamp(prefixe: string, code: string): string {
  return `${prefixe}-${code.replace(/\./g, '-')}`;
}

export function FormulaireDynamique({ formulaire, donnees, onChange, onChampModifie, erreurs, tarifs = {}, pieces = [], prefixe = 'champ', desactive }: ProprietesFormulaireDynamique) {
  const vis = useMemo(() => visibilites(formulaire, donnees), [formulaire, donnees]);
  // Calculs : le moteur recalcule les champs calculés visibles à partir des valeurs typées.
  const calcules = useMemo(() => validerDonnees(formulaire, donnees, { partiel: true, tarifs }).donnees, [formulaire, donnees, tarifs]);
  const modifier = (code: string, v: unknown) => {
    const suivantes = { ...donnees };
    if (v === undefined) delete suivantes[code];
    else suivantes[code] = v;
    onChange(suivantes);
    onChampModifie?.(code);
  };
  return (
    <>
      {formulaire.sections
        .filter((s) => vis.sections.has(s.code))
        .map((s) => (
          <fieldset key={s.code} className={styles.section}>
            <legend>{s.titre}</legend>
            {s.description ? <p className={styles.descriptionSection}>{s.description}</p> : null}
            {s.champs
              .filter((c) => vis.champs.has(c.code))
              .map((c) => (
                <ChampDynamique
                  key={c.code}
                  id={idChamp(prefixe, c.code)}
                  champ={c}
                  valeur={donnees[c.code]}
                  onChange={(v) => modifier(c.code, v)}
                  erreur={erreurs.find((e) => e.champ === c.code)?.message ?? null}
                  erreursSous={erreurs.filter((e) => e.champ.startsWith(`${c.code}.`)).map((e) => ({ champ: e.champ.slice(c.code.length + 1), message: e.message }))}
                  pieces={pieces}
                  valeurCalculee={c.calcul ? ((calcules[c.code] as number | undefined) ?? null) : undefined}
                  desactive={desactive}
                />
              ))}
          </fieldset>
        ))}
    </>
  );
}

/** Retire les erreurs d'un champ (et de ses sous-éléments) dès qu'il est modifié. */
export function sansErreursDe(erreurs: ErreurSaisie[], code: string): ErreurSaisie[] {
  return erreurs.filter((e) => e.champ !== code && !e.champ.startsWith(`${code}.`));
}

/** Erreurs du moteur ramenées aux identifiants des champs de l'écran (résumé avec liens). */
export function erreursPourResume(erreurs: ErreurSaisie[], prefixe: string): { champ: string; message: string }[] {
  return erreurs.map((e) => ({ champ: idChamp(prefixe, e.champ), message: e.message }));
}
